/**
 * Social sign-in — Google, Apple, Microsoft, LinkedIn — for both web portals.
 *
 * One round trip, one set of paths, whatever the provider:
 *
 *   /login or /signup  → provider  → SSO_CALLBACK_PATH (Clerk finishes it)
 *        ↑ an error: Clerk returns here and leaves it on the attempt
 *                                   → SIGN_UP_CONTINUE_PATH when the new
 *                                     account still needs something (an
 *                                     email Clerk must verify — Microsoft's,
 *                                     typically)
 *                                   → the page the person was going to
 *
 * The providers come from `SOCIAL_PROVIDERS` (@tonnie/foundation); the Clerk
 * instance must offer the same ones (`npm run qa:clerk-social-providers`).
 *
 * A session the browser still holds makes Clerk refuse the start with "signed
 * in"; it is signed out and the start runs once more (`retryAfterStaleSession`).
 *
 * Nothing here throws at the caller or renders words: failures are Clerk's
 * errors, for `useAuthErrorMessage` to put into the reader's language.
 */

import { useClerk, useSignIn, useSignUp } from '@clerk/clerk-react';
import {
  SOCIAL_PROVIDER_STRATEGY,
  socialProviderOf,
  type SocialProvider,
} from '@tonnie/foundation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { retryAfterStaleSession } from './stale-session';

/** Where every provider sends the person back; `AuthenticateWithRedirectCallback` lives here. */
export const SSO_CALLBACK_PATH = '/auth/sso-callback';
/** Where Clerk sends a social sign-up that still needs something. */
export const SIGN_UP_CONTINUE_PATH = '/signup/continue';

/**
 * Where the person was going, kept across the provider round trip for
 * SIGN_UP_CONTINUE_PATH — Clerk hands `redirectUrlComplete` to its callback
 * but not to the continue page, and a business sign-up is bound for
 * /onboarding or an invitation, not the fallback. Session-scoped; storage
 * that is unavailable (a private window) only loses the destination.
 */
const COMPLETE_URL_KEY = 'tonnie.social.completeUrl';

function keepCompleteUrl(url: string): void {
  try {
    sessionStorage.setItem(COMPLETE_URL_KEY, url);
  } catch {
    // Storage blocked — the continue page falls back to the caller's default.
  }
}

function takeCompleteUrl(): string | null {
  try {
    return sessionStorage.getItem(COMPLETE_URL_KEY);
  } catch {
    return null;
  }
}

function forgetCompleteUrl(): void {
  try {
    sessionStorage.removeItem(COMPLETE_URL_KEY);
  } catch {
    // Nothing kept, nothing to forget.
  }
}

export type SocialStartOutcome = { outcome: 'redirecting' } | { outcome: 'failed'; error: unknown };

/**
 * Start a social sign-in (`sign-in` page) or sign-up (`sign-up` page). Either
 * way Clerk moves between the two by itself on the way back — an unknown
 * account becomes a sign-up, a known one a sign-in.
 */
export function useSocialSignIn(mode: 'sign-in' | 'sign-up') {
  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const { signUp, isLoaded: signUpLoaded } = useSignUp();
  const clerk = useClerk();
  const ready = mode === 'sign-in' ? signInLoaded && !!signIn : signUpLoaded && !!signUp;

  const start = useCallback(
    async (provider: SocialProvider, completeUrl: string): Promise<SocialStartOutcome> => {
      const params = {
        strategy: SOCIAL_PROVIDER_STRATEGY[provider],
        redirectUrl: SSO_CALLBACK_PATH,
        redirectUrlComplete: completeUrl,
      };
      keepCompleteUrl(completeUrl);
      try {
        if (mode === 'sign-in') {
          if (!signIn) throw new Error('Clerk sign-in is not loaded');
          await retryAfterStaleSession(
            () => signIn.authenticateWithRedirect(params),
            () => clerk.signOut()
          );
        } else {
          if (!signUp) throw new Error('Clerk sign-up is not loaded');
          await retryAfterStaleSession(
            () => signUp.authenticateWithRedirect(params),
            () => clerk.signOut()
          );
        }
        return { outcome: 'redirecting' };
      } catch (error) {
        return { outcome: 'failed', error };
      }
    },
    [mode, signIn, signUp, clerk]
  );

  return { ready, start };
}

/**
 * The error a social sign-in came back with, if any. When the provider round
 * trip fails, Clerk sends the person back to the sign-in / sign-up page and
 * leaves the reason on the attempt — `signIn.firstFactorVerification.error`
 * or `signUp.verifications.externalAccount.error`. Without reading it, the
 * page simply reappears and the person never learns why.
 */
export function useSocialRedirectError(): {
  error: unknown;
  provider: SocialProvider | null;
} | null {
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();

  const fromSignIn = signIn?.firstFactorVerification;
  if (fromSignIn?.error && socialProviderOf(fromSignIn.strategy)) {
    return { error: fromSignIn.error, provider: socialProviderOf(fromSignIn.strategy) };
  }
  const fromSignUp = signUp?.verifications?.externalAccount;
  if (fromSignUp?.error && socialProviderOf(fromSignUp.strategy)) {
    return { error: fromSignUp.error, provider: socialProviderOf(fromSignUp.strategy) };
  }
  return null;
}

/** Where a social sign-up stands when Clerk sends it to SIGN_UP_CONTINUE_PATH. */
export type SignUpContinuation =
  | { step: 'loading' }
  /** No sign-up in progress — the page was opened directly, or it is finished. */
  | { step: 'none' }
  /** The provider's email must be proven: a code went to it. */
  | { step: 'verify_email'; email: string }
  /** Clerk asks for something this product does not collect. Named, for the report. */
  | { step: 'unsupported'; missing: string[] }
  | { step: 'complete' };

export type ContinuationOutcome = { outcome: 'done' } | { outcome: 'failed'; error: unknown };

/**
 * Finish a social sign-up Clerk could not complete on its own. The one gap
 * this instance can leave is an unverified email (Microsoft does not vouch for
 * its addresses; Clerk asks for proof), so a code is sent to it once and the
 * person types it here. Anything else is reported as `unsupported`.
 */
export function useSignUpContinuation() {
  const { signUp, isLoaded, setActive } = useSignUp();
  const [codeSendError, setCodeSendError] = useState<unknown>(null);
  const [completeUrl] = useState(takeCompleteUrl);
  const codeSent = useRef(false);

  const status = signUp?.status ?? null;
  const unverified = signUp?.unverifiedFields ?? [];
  const missing = signUp?.missingFields ?? [];
  const email = signUp?.emailAddress ?? null;

  const state: SignUpContinuation = !isLoaded
    ? { step: 'loading' }
    : !signUp || status === null || status === 'abandoned'
      ? { step: 'none' }
      : status === 'complete'
        ? { step: 'complete' }
        : missing.length === 0 &&
            unverified.length === 1 &&
            unverified[0] === 'email_address' &&
            email
          ? { step: 'verify_email', email }
          : { step: 'unsupported', missing: [...missing, ...unverified] };

  const sendCode = useCallback(async (): Promise<ContinuationOutcome> => {
    if (!signUp) return { outcome: 'failed', error: new Error('No sign-up in progress') };
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setCodeSendError(null);
      return { outcome: 'done' };
    } catch (error) {
      setCodeSendError(error);
      return { outcome: 'failed', error };
    }
  }, [signUp]);

  // The first code goes out on arrival — once.
  useEffect(() => {
    if (state.step === 'verify_email' && !codeSent.current) {
      codeSent.current = true;
      void sendCode();
    }
  }, [state.step, sendCode]);

  const verify = useCallback(
    async (code: string): Promise<ContinuationOutcome> => {
      if (!signUp || !setActive) {
        return { outcome: 'failed', error: new Error('No sign-up in progress') };
      }
      try {
        const result = await signUp.attemptEmailAddressVerification({ code });
        if (result.status !== 'complete' || !result.createdSessionId) {
          return {
            outcome: 'failed',
            error: new Error(`Sign-up still ${result.status} after the email code`),
          };
        }
        await setActive({ session: result.createdSessionId });
        forgetCompleteUrl();
        return { outcome: 'done' };
      } catch (error) {
        return { outcome: 'failed', error };
      }
    },
    [signUp, setActive]
  );

  /** `completeUrl`: where the person was going when they chose the provider, if known. */
  return { state, completeUrl, codeSendError, sendCode, verify };
}
